import React, { useEffect, useState } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import {
  Box, Panel, Row, PageLoading, toast,
} from '@galilee/lilee';
import { useAuth } from 'state/AuthProvider';
import { useMatter } from 'state/MatterProvider';
import { useApplication } from 'state/ApplicationProvider';
import Container from 'components/Container';
import MatterSummary from 'components/MatterSummary';
import StepsToCompletion from 'components/StepsToCompletion';
import { welcomeReadAction, setSubmitMatterAction } from 'actions/Matter';
import LastAction from 'components/LastAction';
import {
  MATTER_STAGES, httpRequest, downloadFile, getSummaryStage,
} from 'utils';
import PROGRESS from 'enums/Progress';
import PartyType from 'enums/PartyType';
import styled from 'styled-components';
import SummaryStatus from '../components/SummaryStatus';
import SubmitPopup from '../components/SubmitPopup';
import WelcomeModal from './components/WelcomeModal';

const VIDEO_WIDGET_URL = process.env.REACT_APP_VIDEO_WIDGET_URL;

const StyledPanel = styled(Panel)`
  margin-bottom: 24px;
  border-radius: 10px;
  box-shadow: 0px 14px 34px rgb(7 36 56 / 10%);
  padding: 0;
`;

const PlayButton = styled.button`
  background: #072438;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
`;

const PlayIcon = styled.span`
  font-size: 20px;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  @keyframes zettle-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Spinner = styled.div`
  position: absolute;
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: #fff;
  border-radius: 50%;
  animation: zettle-spin 0.8s linear infinite;
`;

const VideoContainer = styled.div`
  background: #000;
  border-radius: 12px;
  position: relative;
  overflow: hidden;

  ${(p) => {
    if (p.$hidden) {
      return `
        width: 0;
        height: 0;
        opacity: 0;
        position: absolute;
      `;
    }
    return `
      width: ${p.$landscape ? 'min(90vw, 900px)' : 'min(90vw, 400px)'};
      aspect-ratio: ${p.$landscape ? '16 / 9' : '9 / 16'};
      max-height: 90vh;
    `;
  }}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const WidgetRoot = styled.div`
  width: 100%;
  height: 100%;
`;

const Summary = ({ match }) => {
  const { state, dispatch } = useMatter();
  const { dispatch: applicationDispatch } = useApplication();
  const { user, authToken, support } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const allStagesJustCompleted = state.matterProgress && state.matterProgress.allStagesJustCompleted;
  const [requireDownload, setRequireDownload] = useState(state.matter.isILAIFA && !state.matter.documentsDownloaded
      && getSummaryStage(state.matterProgress, user.id).stage === MATTER_STAGES.NOT_STARTED);
  const [isVideoLightboxOpen, setIsVideoLightboxOpen] = useState(false);
  const [isVideoLandscape, setIsVideoLandscape] = useState(null);

  const currentBorrower = state.matter?.borrowers?.find((b) => b.id === user.id);
  const isGuarantor = currentBorrower?.borrowerType === PartyType.Guarantor;
  const videoStatus = state.matter?.videoStatus;
  const videoId = isGuarantor ? videoStatus?.guarantorVideoId : videoStatus?.borrowerVideoId;
  const segmentDurations = isGuarantor ? videoStatus?.guarantorSegmentDurations : videoStatus?.borrowerSegmentDurations;
  const hasVideo = isGuarantor ? videoStatus?.hasGuarantorVideo : videoStatus?.hasBorrowerVideo;

  useEffect(() => {
    if (allStagesJustCompleted === true) {
      dispatch({ type: 'RESET_ALLSTAGESJUSTCOMPLETED' });
    }
  }, [allStagesJustCompleted, dispatch]);

  useEffect(() => {
    const existingScript = document.getElementById('zettle-advice-video-widget-script');
    const existingStylesheet = document.getElementById('zettle-advice-video-widget-stylesheet');

    if (!isVideoLightboxOpen) {
      if (existingScript) existingScript.remove();
      if (existingStylesheet) existingStylesheet.remove();
      return undefined;
    }

    if (!videoId || !VIDEO_WIDGET_URL) return undefined;

    const root = document.getElementById('zettle-advice-video-widget-root');
    if (!root) return undefined;

    if (!existingStylesheet) {
      const stylesheet = document.createElement('link');
      stylesheet.id = 'zettle-advice-video-widget-stylesheet';
      stylesheet.rel = 'stylesheet';
      stylesheet.href = `${VIDEO_WIDGET_URL}/zettleadvicevideowidget.css`;
      document.head.appendChild(stylesheet);
    }

    if (!existingScript) {
      const script = document.createElement('script');
      const encodedVideoId = encodeURIComponent(videoId);
      const timings = encodeURIComponent(JSON.stringify(segmentDurations || []));
      script.id = 'zettle-advice-video-widget-script';
      script.src = `${VIDEO_WIDGET_URL}/zettle-advice-video-widget.js?video-id=${encodedVideoId}&video-segment-timings=${timings}`;
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      const s = document.getElementById('zettle-advice-video-widget-script');
      const ss = document.getElementById('zettle-advice-video-widget-stylesheet');
      if (s) s.remove();
      if (ss) ss.remove();
    };
  }, [isVideoLightboxOpen, videoId, segmentDurations]);

  useEffect(() => {
    if (!isVideoLightboxOpen) {
      setIsVideoLandscape(null);
      return undefined;
    }
    const root = document.getElementById('zettle-advice-video-widget-root');
    if (!root) return undefined;
    const handler = (e) => setIsVideoLandscape(e.detail.landscape);
    root.addEventListener('zettle-orientation', handler);
    return () => root.removeEventListener('zettle-orientation', handler);
  }, [isVideoLightboxOpen]);

  const doDownload = () => {
    if (downloading) return;
    setDownloading(true);
    httpRequest(`/api/matters/${state.matter.matterId}/submitteddocuments`, 'get', null, { Authorization: `Bearer ${authToken}` })
      .then((blob) => {
        downloadFile(blob, 'LoanApplication.zip');
      })
      .catch(() => {
        throw new Error('Unable to download your documents at the moment, please contact support for assistance.');
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const setPrintDocsCompleted = () => {
    const ilIFASignDocs = state.matterProgress?.signDocuments?.filter((x) => x.requiresPrinting === true);
    ilIFASignDocs?.forEach((doc) => {
      dispatch({
        type: 'SET_OPTIMISTIC_PROGRESS',
        payload: {
          theirProgress: PROGRESS.COMPLETED,
          documentId: doc.id,
          stage: 'sign',
          progress: {
            myProgress: PROGRESS.COMPLETED,
            theirProgress: PROGRESS.COMPLETED,
          },
        },
      });
    });
  };

  if (!state || !state.matter || !state.matterProgress) return <PageLoading />;

  const onSubmitDocuments = async () => {
    const { matterId } = state.matter;

    try {
      await setSubmitMatterAction(matterId, authToken);
      toast.success('Matter submitted successfully.');
    } catch (error) {
      toast.error('We couldn\'t submit your documents at the moment, please reload the page to try again.');
    }
  };

  const onWelcomeModalConfirm = () => {
    welcomeReadAction(dispatch, applicationDispatch, state.matter.matterId, authToken, user.id);
  };

  const showWelcomeModal = (state.matter?.borrowers?.some((b) => b.id === user.id && b.shownWelcomeScreen === false)) && !support.isTenantSupport;
  const requireVerification = state.matter?.requiresVerification;

  return (
    <Container center>
      <SubmitPopup status={getSummaryStage(state.matterProgress, user.id).stage} onSubmitDocuments={onSubmitDocuments} dateToAutoSubmit={state.dateToAutoSubmit} />
      <Box p={[3, 0]}>
        <Row collapseIndex="0">
          <SummaryStatus
            matter={state}
            match={match}
            user={user}
            onSubmitDocuments={onSubmitDocuments}
            onDownloadDocuments={doDownload}
            requireDownload={requireDownload}
            setRequireDownload={setRequireDownload}
            setPrintDocsCompleted={setPrintDocsCompleted}
          />
        </Row>
        {hasVideo && (
          <StyledPanel>
            <Box p={[7, 7, 8]}>
              <PlayButton type="button" onClick={() => setIsVideoLightboxOpen(true)}>
                Watch your video summary
                <PlayIcon>&#9654;</PlayIcon>
              </PlayButton>
            </Box>
          </StyledPanel>
        )}
        <StyledPanel>
          <Box p={[7, 7, 8]}>
            <MatterSummary matter={state.matter} />
          </Box>
        </StyledPanel>
        {isVideoLightboxOpen && (
          <LightboxOverlay onClick={() => setIsVideoLightboxOpen(false)} role="dialog" aria-modal="true">
            {isVideoLandscape === null && <Spinner />}
            <VideoContainer
              onClick={(e) => e.stopPropagation()}
              $hidden={isVideoLandscape === null}
              $landscape={isVideoLandscape}
            >
              <CloseButton type="button" onClick={() => setIsVideoLightboxOpen(false)} aria-label="Close">
                &times;
              </CloseButton>
              <WidgetRoot id="zettle-advice-video-widget-root" />
            </VideoContainer>
          </LightboxOverlay>
        )}
        { !requireDownload && (
        <StyledPanel>
          <Box p={[7, 7, 8]}>
            <StepsToCompletion matchUrl={match.url} />
          </Box>
        </StyledPanel>
        )}
        {showWelcomeModal && <WelcomeModal requireVerification={requireVerification} onConfirm={onWelcomeModalConfirm} />}
        <LastAction lastAction={state.matter.lastAction} path={`/matter/${state.matter.matterId}/security`} />
      </Box>
    </Container>
  );
};

Summary.propTypes = {
  match: ReactRouterPropTypes.match.isRequired,
};

export default Summary;
